import { useState } from 'react';
import type { Message } from '../../../hooks/useChatHistory';
import type { MemorySuggestion } from '../../../hooks/useMemoryExtraction';

interface ChatInterfaceProps {
  messages: Message[];
  isTyping: boolean;
  bottomRef: React.RefObject<HTMLDivElement>;
  onMemoryConfirm?: (suggestion: MemorySuggestion) => void;
  onMemorySkip?: (suggestion: MemorySuggestion) => void;
  onMemoryLater?: (suggestion: MemorySuggestion) => void;
  onReaction?: (emoji: string, messageIndex: number, messageText: string) => void;
}

const REACTION_EMOJIS = ['❤️', '⭐', '😊', '😂', '🥺', '👍'];

export default function ChatInterface({
  messages,
  isTyping,
  bottomRef,
  onReaction,
}: ChatInterfaceProps) {
  const [activeReactionMenu, setActiveReactionMenu] = useState<number | null>(null);
  const [reactionedMessages, setReactionedMessages] = useState<Record<number, string>>({});

  const handleReactionClick = (emoji: string, idx: number, text: string) => {
    setReactionedMessages(prev => ({ ...prev, [idx]: emoji }));
    setActiveReactionMenu(null);
    onReaction?.(emoji, idx, text);
  };

  return (
    <div
      className="flex-1 overflow-y-auto px-3 py-4 space-y-2"
      onClick={() => setActiveReactionMenu(null)}
    >
      {messages.map((msg, idx) => {
        if (msg.type === 'system') {
          return (
            <div key={idx} className="flex justify-center my-3">
              <div className="bg-black/10 backdrop-blur-sm text-gray-600 text-[11px] px-4 py-1.5 rounded-full shadow-sm">
                {msg.text}
              </div>
            </div>
          );
        }

        const isAI = msg.type === 'ai';
        const hasReaction = reactionedMessages[idx];

        return (
          <div
            key={idx}
            className={`flex ${isAI ? 'justify-start' : 'justify-end'} items-end gap-2 group`}
          >
            {/* AIアバター */}
            {isAI && (
              <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mb-1 shadow-sm border border-white/50">
                <img
                  src="https://static.readdy.ai/image/72fcb6eaf5872ea13294af8c44706fad/a24adec9501d50c6a1b55366ad633d8e.png"
                  alt="いのちゃん"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: '50% 8%', transform: 'scale(1.2)', transformOrigin: '50% 8%' }}
                />
              </div>
            )}

            <div className={`flex flex-col ${isAI ? 'items-start' : 'items-end'} max-w-[72%] relative`}>
              {/* メッセージ吹き出し */}
              <div
                className={`relative px-3.5 py-2.5 rounded-2xl shadow-sm cursor-pointer select-none ${
                  isAI
                    ? 'bg-white text-gray-800 rounded-tl-sm'
                    : 'text-white rounded-tr-sm'
                }`}
                style={!isAI ? { background: 'linear-gradient(135deg, #06C755 0%, #05a847 100%)' } : {}}
                onDoubleClick={() => isAI && setActiveReactionMenu(activeReactionMenu === idx ? null : idx)}
              >
                <p className="text-[14.5px] leading-relaxed whitespace-pre-wrap break-words">
                  {msg.text}
                </p>

                {/* リアクション済み表示 */}
                {hasReaction && (
                  <div className={`absolute -bottom-3 ${isAI ? 'left-2' : 'right-2'} bg-white rounded-full px-1.5 py-0.5 shadow-md border border-gray-100 text-sm`}>
                    {hasReaction}
                  </div>
                )}
              </div>

              {/* タイムスタンプ + リアクションボタン */}
              <div className={`flex items-center gap-1.5 mt-1 px-1 ${isAI ? 'flex-row' : 'flex-row-reverse'}`}>
                {msg.time && (
                  <span className="text-[10px] text-gray-400/80">
                    {msg.time}
                  </span>
                )}
                {!isAI && (
                  <span className="text-[10px] text-gray-400/70 flex items-center gap-0.5">
                    <i className="ri-check-double-line text-[#06C755]"></i>
                  </span>
                )}

                {/* リアクションボタン（AIメッセージのみ・ホバー時） */}
                {isAI && onReaction && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveReactionMenu(activeReactionMenu === idx ? null : idx);
                    }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-5 h-5 flex items-center justify-center rounded-full bg-white/80 shadow-sm hover:bg-white cursor-pointer"
                  >
                    <i className="ri-emotion-line text-gray-400 text-xs"></i>
                  </button>
                )}
              </div>

              {/* リアクションメニュー */}
              {activeReactionMenu === idx && isAI && (
                <div
                  className="absolute bottom-8 left-0 z-20 bg-white rounded-2xl shadow-xl border border-gray-100 px-2 py-1.5 flex gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  {REACTION_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => handleReactionClick(emoji, idx, msg.text)}
                      className="w-8 h-8 flex items-center justify-center text-lg hover:scale-125 hover:bg-gray-50 rounded-full transition-all duration-150 cursor-pointer"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* タイピングインジケーター */}
      {isTyping && (
        <div className="flex justify-start items-end gap-2">
          <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mb-1 shadow-sm border border-white/50">
            <img
              src="https://static.readdy.ai/image/72fcb6eaf5872ea13294af8c44706fad/a24adec9501d50c6a1b55366ad633d8e.png"
              alt="いのちゃん"
              className="w-full h-full object-cover"
              style={{ objectPosition: '50% 8%', transform: 'scale(1.2)', transformOrigin: '50% 8%' }}
            />
          </div>
          <div className="bg-white px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm">
            <div className="flex gap-1.5 items-center">
              <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
